import { useContext, useEffect, useState } from 'react';
import { MuiChipsInput } from 'mui-chips-input';
import { Grid, Typography, Button } from '@mui/material';
import { toast } from 'react-toastify';
import CircleIcon from '@mui/icons-material/Circle';
import UpdateIcon from '@mui/icons-material/Update';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ViewPlaylistModal from './ViewPlaylistModal';
import { Modalv2Context } from '../../../../../../Contexts/Modalv2Context';
import SwitchInput from '../../../../../../Components/SwitchInput';
import { updatePlaylistMapping, remapPlaylist } from '../../../../../../api/contentMapping';
import { UnitsContext } from '../../../../../../Contexts/UnitsContext';
import { ConfigContext } from '../../../../../../Contexts/ConfigContext';
import { getStatusColor, getJsonDiff } from '../../../../../../utilities/helpers';
import { UserContext } from '../../../../../../Contexts/UserContext';
import { Box } from '@mui/system';
import useCommonStyles from '../../../../../../commonStyles';
import DropDownSingle from '../../../../../../Components/DropDownSingle';
import InputConfig from '../../../../../../Components/InputConfig';
import { getVideos } from '../../../../../../api/video-library';
import { AUDIO_VERSION, CTA_TYPES } from '../../../../../../core/constants';

const ctaTypesList = [CTA_TYPES.PLAYLIST_URL, CTA_TYPES.CUSTOM_PLAYLIST_CTA, CTA_TYPES.SECTION_LVL];

/**
 * Component for Playlist Card representing individual mapped playlist.
 */
const PlaylistCard = ({ playlistDetails, index, assignedPlaylistId }) => {
	const { updateModalContent } = useContext(Modalv2Context);
	const { siteRawConfig, updatePlaylist, getFinalOverviewObj, previousCouchBaseConfig } =
		useContext(UnitsContext);
	const { rawConfig, updateRawConfig } = useContext(ConfigContext);
	const { user } = useContext(UserContext);
	const commonStyleClasses = useCommonStyles();
	const [mutedAudioVersionExist, setMutedAudioVersionExist] = useState(false);
	const [audioVersion, setAudioVersion] = useState(playlistDetails?.activeAudioVersion);
	const additionalOwnerEmails = playlistDetails.ownerEmails.filter(
		(email) => email !== siteRawConfig.ownerEmail
	);

	/**
	 * This function iterates in the video category doc to find if every video in YouTube playlist to have a muted counterpart,
	 * If they have muted counterpart then it sets the mutedAudioVersionExist state to true else it remains false.
	 */
	useEffect(() => {
		try {
			const checkIfMutedAudioVersionExist = async () => {
				let { data } = await getVideos(playlistDetails.category);
				const videosData = data?.data?.videos;
				const videosForCurrentPlaylist = videosData.filter(
					(videoObj) => videoObj[AUDIO_VERSION.MUTED]?.playlistId === playlistDetails?.playlistId
				);
				if (videosForCurrentPlaylist.length > 0) {
					const doesMutedAudioVersionExist = videosForCurrentPlaylist.every(
						(videoObj) =>
							videoObj[AUDIO_VERSION.MUTED] && videoObj[AUDIO_VERSION.MUTED]?.cdn77PathUrl !== ''
					);
					setMutedAudioVersionExist(doesMutedAudioVersionExist);
				}
			};
			checkIfMutedAudioVersionExist();
		} catch (error) {
			console.log('Error Occurred While Checking For Muted Audio Version -> ', error);
		}
		
	}, []);

	const audioVersionDataArray = [
		{ label: 'unmuted', value: 'unmuted', disable: false },
		{ label: 'muted', value: 'muted', disable: !mutedAudioVersionExist } // disabling muted option in dropdown if it doesn't exist.
	];

	/**
	 * Function to update the ownerEmails in playlist Object provided the updatedOwnerEmailsArray
	 */
	const updateOwnerEmails = (updatedOwnersEmailsArray) => {
		playlistDetails.ownerEmails = [siteRawConfig.ownerEmail, ...updatedOwnersEmailsArray];
		updatePlaylist(index, 'ownerEmails', playlistDetails.ownerEmails);
	};

	/**
	 * Function to save/update individual playlist changes
	 */
	const savePlaylistChanges = async () => {
		try {
			const payload = { data: playlistDetails };
			const maxPlaylistVideos = playlistDetails?.maxPlaylistVideos;

			if (
				typeof maxPlaylistVideos === 'number' &&
				(maxPlaylistVideos > 10 || maxPlaylistVideos < 1)
			) {
				throw new Error('Playlist Max Videos should be between 1-10');
			}

			validatePlaylistCtaDetails(playlistDetails, rawConfig);

			const response = await updatePlaylistMapping(payload);
			toast.success(response?.data?.data?.message, { position: toast.POSITION.BOTTOM_RIGHT });
		} catch (error) {
			const errMessage = error?.response?.data?.data?.message || error.message;
			toast.error(errMessage || 'Some error occurred', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		}
	};

	/**
	 * @name validatePlaylistCtaDetails
	 * @description This function validates the playlist cta details based on the cta type selected for the playlist.
	 * @param  playlistData
	 * @param videoSection
	 * @returns
	 */
	const validatePlaylistCtaDetails = (playlistData, rawVideoSection) => {
		const sectionLvlCtaText = rawVideoSection['sectionLvlCtaDetails.ctaText'];
		const sectionLvlCtaUrl = rawVideoSection['sectionLvlCtaDetails.ctaUrl'];
		const ctaTypeSelectedForThisPlaylist =
			rawVideoSection[`sectionLvlCtaDetails.playListDetails.${playlistData.playlistId}.ctaType`];

		/**
		 * If site doc changes are made , user needs to update those first.
		 * Reason : playlist cta type selected is site specific feature [the cta type is in the site doc]
		 * hence it needs to be updated before updating the playlist object.
		 */
		const siteDocChangesMade =
			Object.keys(getJsonDiff(previousCouchBaseConfig, getFinalOverviewObj())).length > 0;
		if (siteDocChangesMade) {
			throw new Error(
				`Site Doc Changes have been made,
				or playlist cta type has been updated (which is site specific).
				Please update the site doc first.`
			);
		}

		//If type is section level then section level cta details must be present;
		if (ctaTypeSelectedForThisPlaylist === CTA_TYPES.SECTION_LVL) {
			if (!sectionLvlCtaText || !sectionLvlCtaUrl) {
				throw new Error('Section Level Cta Details must be present for this playlist');
			}
			if (sectionLvlCtaText.length > 14) {
				throw new Error('Section lvl ctaText length should be less than 15 characters');
			}
		}

		//If type is custom playlist cta then custom playlist cta details must be present;
		if (ctaTypeSelectedForThisPlaylist === CTA_TYPES.CUSTOM_PLAYLIST_CTA) {
			const customPlaylistCtaDetails = playlistData?.customPlaylistCtaDetails || {};
			if (!customPlaylistCtaDetails?.ctaText || !customPlaylistCtaDetails?.ctaUrl) {
				throw new Error('Custom Playlist Cta Details must be present for this playlist');
			}
			if (customPlaylistCtaDetails.ctaText.length > 14) {
				throw new Error('Custom playlist ctaText length should be less than 15 characters');
			}
		}
	};

	/**
	 * Function to trigger remapping of a playlist
	 */
	const remapPlaylistHandler = async () => {
		try {
			const response = await remapPlaylist(playlistDetails.playlistId);
			toast.success(response?.data?.data?.message, { position: toast.POSITION.BOTTOM_RIGHT });
		} catch (error) {
			const errMessage = error?.response?.data?.data?.message || error.message;
			toast.error(errMessage || 'Some error occurred', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		}
	};

	const handleAudioVersionChange = (audioValue) => {
		setAudioVersion(audioValue);
		updatePlaylist(index, 'activeAudioVersion', audioValue);
	};

	/**
	 * @name handleCustomPlaylistCtaDetailsChange
	 * @description Function to handle the change in custom playlist cta details.
	 * @param {number} index
	 * @param {"ctaUrl"|"ctaText"} propertyName
	 * @param {string} value
	 */
	const handleCustomPlaylistCtaDetailsChange = (index, propertyName, value) => {
		const clonedPlaylistDetails = structuredClone(playlistDetails);
		const customPlaylistCtaDetails = clonedPlaylistDetails.customPlaylistCtaDetails || {};
		customPlaylistCtaDetails[propertyName] = value;
		updatePlaylist(index, 'customPlaylistCtaDetails', customPlaylistCtaDetails);
	};

	const videosList =
		audioVersion === AUDIO_VERSION.UNMUTED
			? playlistDetails?.unmutedVideosList
			: playlistDetails?.mutedVideosList;

	const conditionalBgColor = assignedPlaylistId === playlistDetails.playlistId ? '#f7fff5' : '';

	const customPlaylistCtaDetailsEditable =
		rawConfig[`sectionLvlCtaDetails.playListDetails.${playlistDetails.playlistId}.ctaType`] ===
		CTA_TYPES.CUSTOM_PLAYLIST_CTA;

	return (
		<Box
			className={commonStyleClasses.basicOutlinedBox}
			style={{
				width: '100%',
				backgroundColor: conditionalBgColor
			}}
			key={playlistDetails?.playlistId}
		>
			<Grid container spacing={2}>
				<Grid item xs={6}>
					<Typography variant="h6" fontWeight={600}>
						{playlistDetails.playlistName}
					</Typography>
					<small style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
						<CircleIcon fontSize="2rem" color={getStatusColor(playlistDetails.processStatus)} />
						{playlistDetails.processStatus}
					</small>
				</Grid>

				<Grid item xs={6} style={{ display: 'flex' }}>
					<Button onClick={remapPlaylistHandler}>
						<Typography
							color="primary"
							variant="h6"
							fontWeight={600}
							style={{ display: 'flex', alignItems: 'center', marginLeft: '1rem', gap: '1rem' }}
						>
							<UpdateIcon />
							Remap
						</Typography>
					</Button>
					<SwitchInput
						label="Public"
						disabled={!user.isSuperAdmin}
						isObjectEditor={false}
						containerStyles={{ marginTop: '1rem', width: 'fit-content' }}
						directValue={playlistDetails.isPublic}
						updateDirectValue={() => updatePlaylist(index, 'isPublic', !playlistDetails.isPublic)}
					/>
				</Grid>
				<Grid item xs={4}>
					<InputConfig
						keyData="playlistDetails.customPlaylistCtaDetails.ctaText"
						label="Custom Cta Text"
						isObjectEditor={false}
						directValue={playlistDetails?.customPlaylistCtaDetails?.ctaText}
						updateDirectValue={(value) =>
							handleCustomPlaylistCtaDetailsChange(index, 'ctaText', value)
						}
						editable={customPlaylistCtaDetailsEditable}
					/>
				</Grid>
				<Grid item xs={4}>
					<InputConfig
						keyData="playlistDetails.customPlaylistCtaDetails.ctaUrl"
						label="Custom Cta Url"
						isObjectEditor={false}
						directValue={playlistDetails?.customPlaylistCtaDetails?.ctaUrl}
						updateDirectValue={(value) =>
							handleCustomPlaylistCtaDetailsChange(index, 'ctaUrl', value)
						}
						editable={customPlaylistCtaDetailsEditable}
					/>
				</Grid>

				<Grid item xs={4}>
					<InputConfig
						label="Max Videos"
						isObjectEditor={false}
						inputType={'number'}
						directValue={playlistDetails?.maxPlaylistVideos}
						updateDirectValue={(value) => updatePlaylist(index, 'maxPlaylistVideos', value)}
						placeholder="5"
					/>
				</Grid>

				<Grid item xs={4} style={{ display: 'flex', justifyContent: 'stretch' }}>
					<DropDownSingle
						label={'Active Audio Version'}
						labelStyles={{ fontSize: '1.5rem' }}
						isObjectEditor={false}
						directValue={playlistDetails?.activeAudioVersion}
						updateDirectValue={(audioValue) => handleAudioVersionChange(audioValue)}
						list={audioVersionDataArray}
						formControlStyles={{ width: '100%' }}
						containerStyles={{ width: '20rem' }}
						formControlSize={'small'}
					/>
				</Grid>

				<Grid item xs={4} style={{ display: 'flex', justifyContent: 'stretch' }}>
					<DropDownSingle
						label="Playlist Cta Type"
						labelStyles={{ fontSize: '1.5rem' }}
						list={ctaTypesList}
						object={rawConfig}
						updateObject={updateRawConfig}
						keyData={`sectionLvlCtaDetails.playListDetails.${playlistDetails.playlistId}.ctaType`}
						formControlStyles={{ width: '100%' }}
						containerStyles={{ width: '20rem' }}
						formControlSize={'small'}
						forceDeselect={true}
					/>
				</Grid>

				<Grid item xs={12}>
					<MuiChipsInput
						label="Add Owner Emails"
						color="primary"
						value={additionalOwnerEmails}
						onChange={updateOwnerEmails}
						sx={{ width: '100%' }}
						disabled={!user.isSuperAdmin}
					/>
				</Grid>

				<Grid item xs={5}>
					<Button
						variant="outlined"
						style={{ float: 'left', gap: '0.2rem' }}
						onClick={() =>
							updateModalContent(
								<ViewPlaylistModal
									playlistName={playlistDetails?.playlistName}
									videosList={videosList}
									lastModified={playlistDetails?.lastModified}
								/>
							)
						}
					>
						<VisibilityIcon fontSize="10px" />
						View
					</Button>
					<Button
						variant="contained"
						style={{ float: 'left', gap: '0.2rem', marginLeft: '2rem' }}
						onClick={savePlaylistChanges}
					>
						<SaveIcon fontSize="10px" />
						Save
					</Button>
				</Grid>
			</Grid>
		</Box>
	);
};

export default PlaylistCard;
